import type { UseQueryResult } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  ReleaseFlag,
  useReleaseManagement,
} from "@/utility/feature-management";
import type { BookingTabDescriptor } from "./bookingTabs";
import { BookingErrorState, BookingLoadingState } from "./BookingLoadStates";
import { BookingViewList } from "./BookingViewList";
import type { LiveBookingChanges } from "./getLiveBookingChanges";
import {
  type BookingViewSnapshot,
  useCoreBookingView,
} from "./useCoreBookingView";

interface BookingViewContentProps {
  query: UseQueryResult<BookingViewSnapshot, Error>;
  snapshot: BookingViewSnapshot | null;
  liveChanges: LiveBookingChanges | null;
  meetGreetCollectionDeliveryEnabled: boolean;
  animationsEnabled: boolean;
  liveUpdatesEnabled: boolean;
  animateInitialEntry: boolean;
  onInitialEntryStarted?(): void;
}

const BookingViewContent = ({
  query,
  snapshot,
  liveChanges,
  meetGreetCollectionDeliveryEnabled,
  animationsEnabled,
  liveUpdatesEnabled,
  animateInitialEntry,
  onInitialEntryStarted,
}: BookingViewContentProps) => {
  const { t } = useTranslation();
  const displayedSnapshot = snapshot ?? query.data;

  if (!query.isFetchedAfterMount || displayedSnapshot == null) {
    if (query.isFetching || !query.isError) {
      return <BookingLoadingState />;
    }

    return <BookingErrorState onRetry={() => void query.refetch()} />;
  }

  return (
    <BookingViewList
      bookings={displayedSnapshot.items}
      animationsEnabled={animationsEnabled}
      liveUpdatesEnabled={liveUpdatesEnabled}
      liveChanges={liveChanges}
      animateInitialEntry={animateInitialEntry}
      onInitialEntryStarted={onInitialEntryStarted}
      meetGreetCollectionDeliveryEnabled={meetGreetCollectionDeliveryEnabled}
      emptyMessage={t("input.validations.noBookings")}
    />
  );
};

interface BookingTabContentProps {
  tab: BookingTabDescriptor;
  liveUpdatesEnabled: boolean;
  animationsEnabled?: boolean;
  animateInitialEntry?: boolean;
  onInitialEntryStarted?(): void;
}

export const BookingTabContent = ({
  tab,
  liveUpdatesEnabled,
  animationsEnabled = false,
  animateInitialEntry = false,
  onInitialEntryStarted,
}: BookingTabContentProps) => {
  const { query, presentedSnapshot, liveChanges } = useCoreBookingView(
    tab.view,
    tab.liveUpdateTypes,
    liveUpdatesEnabled,
    animationsEnabled,
  );
  const isMeetGreetCollectionDeliveryV2Enabled = useReleaseManagement(
    ReleaseFlag.releaseCollectionDeliveryMeetGreetV2,
  );
  const isMeetGreetBookingCardsEnabled = useReleaseManagement(
    ReleaseFlag.releaseMeetGreetBookingCards,
  );
  return (
    <BookingViewContent
      query={query}
      snapshot={presentedSnapshot}
      liveChanges={liveChanges}
      meetGreetCollectionDeliveryEnabled={
        isMeetGreetCollectionDeliveryV2Enabled &&
        isMeetGreetBookingCardsEnabled
      }
      animationsEnabled={animationsEnabled}
      liveUpdatesEnabled={liveUpdatesEnabled}
      animateInitialEntry={animateInitialEntry}
      onInitialEntryStarted={onInitialEntryStarted}
    />
  );
};
